import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, ClipboardCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

const CELL = {
  signed: "bg-lime/30 text-navy",
  sent: "bg-teal/15 text-teal",
  packet_opened: "bg-linen text-ink-500",
  not_started: "bg-white text-ink-400 border border-dashed border-linen-border",
};
const SHORT = { signed: "Signed", sent: "Sent", packet_opened: "Opened", not_started: "—" };

const monthShort = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "short" });
};

export const BDMFilingsPanel = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/bdm-filings?months=6`, { headers: authHeader() });
      setData(d);
    } catch {
      setData({ rows: [], months: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="py-16 flex justify-center rounded-2xl bg-white border border-linen-border">
        <Loader2 className="animate-spin text-teal" size={24} />
      </div>
    );
  }
  if ((data.rows || []).length === 0) return null;

  const months = [...(data.months || [])].reverse(); // oldest → newest, reads left to right
  const lastClosed = data.months?.[1];
  const stragglers = (data.rows || []).filter((r) => {
    const cell = r.cells.find((c) => c.month === lastClosed);
    // Only a straggler if they actually logged work that month — nothing logged, nothing to file.
    return r.active && cell && cell.logged > 0 && cell.status !== "signed";
  });

  return (
    <div className="space-y-4" data-testid="admin-bdm-filings-panel">
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
          <ClipboardCheck size={13} /> Filing tracker
        </div>
        <h3 className="mt-1 font-heading font-medium text-xl text-navy">Who's filed, who hasn't</h3>
        <p className="mt-1 text-sm text-ink-500">
          “Opened” is automatic — we know they looked at their packet. “Sent” and “Signed” are marked by the
          partner, because it's their signature. The number under each is what they logged that month.
        </p>
        {stragglers.length > 0 && (
          <p className="mt-3 inline-flex items-start gap-1.5 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2" data-testid="admin-filings-stragglers">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>{stragglers.length}</strong> unsigned for {lastClosed}: {stragglers.map((s) => s.name).join(", ")}
            </span>
          </p>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-filings-grid">
          <thead>
            <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
              <th className="p-3 font-medium">Partner</th>
              {months.map((m) => (
                <th key={m} className="p-3 font-medium text-center whitespace-nowrap">
                  {monthShort(m)}
                  {m === data.current_month ? <span className="text-teal"> (now)</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.bdm_id} className="border-b border-linen-border/60" data-testid={`admin-filing-row-${r.bdm_id}`}>
                <td className="p-3">
                  <span className="font-medium text-navy">{r.name}</span>
                  {!r.active && <span className="ml-2 text-[10px] uppercase tracking-widest text-ink-400">Paused</span>}
                </td>
                {months.map((m) => {
                  const cell = r.cells.find((c) => c.month === m) || { status: "not_started", logged: 0 };
                  return (
                    <td key={m} className="p-2 text-center" data-testid={`admin-filing-cell-${r.bdm_id}-${m}`}>
                      <span
                        title={cell.pandadoc_ref ? `Ref ${cell.pandadoc_ref}` : ""}
                        className={`inline-block w-full max-w-[86px] text-[10px] uppercase tracking-widest px-2 py-1 rounded-md ${CELL[cell.status]}`}
                      >
                        {SHORT[cell.status]}
                      </span>
                      <div className="mt-1 text-[10px] text-ink-400 tabular-nums">{cell.logged || 0} logged</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BDMFilingsPanel;
